import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { theme } from '@/constants/theme';
import { loadPracticeCenterData } from '@/features/unified-learning/runtime/practiceCenterService';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';
import { RuntimeSessionGuard } from '@/features/unified-learning/runtime/screens/RuntimeSessionGuard';
import { RuntimeAsyncState } from '@/features/unified-learning/runtime/components/RuntimeAsyncState';
import { buildRuntimeExamSessionPath, buildRuntimeHomePath, type RuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';
import BorderGlow from '@/components/effects/BorderGlow';
import ElectricBorder from '@/components/effects/ElectricBorder';
import ClickSpark from '@/components/effects/ClickSpark';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

function PulseLoader({ color = PRIMARY }: { color?: string }) {
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 59;
  const g = parseInt(hex.slice(2, 4), 16) || 130;
  const b = parseInt(hex.slice(4, 6), 16) || 246;

  const ring1Scale = useSharedValue(0.4);
  const ring1Opacity = useSharedValue(0.8);
  const ring2Scale = useSharedValue(0.4);
  const ring2Opacity = useSharedValue(0.6);
  const coreScale = useSharedValue(1);

  useEffect(() => {
    ring1Scale.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 900, easing: Easing.out(Easing.cubic) }),
        withTiming(0.4, { duration: 0 }),
      ),
      -1,
      false,
    );
    ring1Opacity.value = withRepeat(
      withSequence(
        withTiming(0, { duration: 900, easing: Easing.out(Easing.cubic) }),
        withTiming(0.8, { duration: 0 }),
      ),
      -1,
      false,
    );
    ring2Scale.value = withDelay(
      300,
      withRepeat(
        withSequence(
          withTiming(1, { duration: 900, easing: Easing.out(Easing.cubic) }),
          withTiming(0.4, { duration: 0 }),
        ),
        -1,
        false,
      ),
    );
    ring2Opacity.value = withDelay(
      300,
      withRepeat(
        withSequence(
          withTiming(0, { duration: 900, easing: Easing.out(Easing.cubic) }),
          withTiming(0.6, { duration: 0 }),
        ),
        -1,
        false,
      ),
    );
    coreScale.value = withRepeat(
      withSequence(
        withTiming(1.2, { duration: 650, easing: Easing.inOut(Easing.sin) }),
        withTiming(0.85, { duration: 650, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      false,
    );
  }, []);

  const r1Style = useAnimatedStyle(() => ({
    opacity: ring1Opacity.value,
    transform: [{ scale: ring1Scale.value }],
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 0 ${Math.round(ring1Opacity.value * 22)}px rgba(${r},${g},${b},0.5)` } as any)
      : {}),
  }));
  const r2Style = useAnimatedStyle(() => ({
    opacity: ring2Opacity.value,
    transform: [{ scale: ring2Scale.value }],
  }));
  const coreStyle = useAnimatedStyle(() => ({
    transform: [{ scale: coreScale.value }],
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 0 14px rgba(${r},${g},${b},0.7)` } as any)
      : {}),
  }));

  return (
    <View style={ls.wrap}>
      <Animated.View style={[ls.ring, { borderColor: `rgba(${r},${g},${b},0.6)` }, r1Style]} />
      <Animated.View style={[ls.ring2, { borderColor: `rgba(${r},${g},${b},0.35)` }, r2Style]} />
      <Animated.View style={[ls.core, { backgroundColor: color }, coreStyle]} />
    </View>
  );
}

const ls = StyleSheet.create({
  wrap: { width: 52, height: 52, alignItems: 'center', justifyContent: 'center' },
  ring: {
    position: 'absolute',
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1.5,
    top: 4,
    left: 4,
  },
  ring2: {
    position: 'absolute',
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 1,
    top: 0,
    left: 0,
  },
  core: {
    width: 13,
    height: 13,
    borderRadius: 6.5,
  },
});

function AnimatedCard({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const mountY = useSharedValue(14);
  const mountOpacity = useSharedValue(0);

  useEffect(() => {
    mountOpacity.value = withDelay(delay, withTiming(1, { duration: 360, easing: Easing.out(Easing.cubic) }));
    mountY.value = withDelay(delay, withSpring(0, { damping: 18, stiffness: 130 }));
  }, [delay]);

  const style = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [{ translateY: mountY.value }],
  }));

  return <Animated.View style={style}>{children}</Animated.View>;
}

export function RuntimeExamModeScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const { slug, uid, token } = useLocalSearchParams<{ slug: string; uid: string; token?: string }>();
  const safeSlug = typeof slug === 'string' ? slug : '';
  const safeUid = typeof uid === 'string' ? uid : '';
  const runtimeSession = { uid: safeUid, token: token ? String(token) : undefined };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof loadPracticeCenterData>> | null>(null);

  const screenOpacity = useSharedValue(0);
  const screenY = useSharedValue(14);

  const load = useCallback(async () => {
    if (!safeSlug || !safeUid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await loadPracticeCenterData(safeSlug, safeUid);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת בדיקת מוכנות');
    } finally {
      setLoading(false);
    }
  }, [safeSlug, safeUid]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!safeSlug || !safeUid) return;
    void trackKursiPlayEvent('learn_exam_entered', { slug: safeSlug, uid: safeUid, source: 'exam_mode_screen' });
  }, [safeSlug, safeUid]);

  useEffect(() => {
    if (!loading) {
      screenOpacity.value = withTiming(1, { duration: 440, easing: Easing.out(Easing.cubic) });
      screenY.value = withSpring(0, { damping: 20, stiffness: 110 });
    }
  }, [loading]);

  const screenStyle = useAnimatedStyle(() => ({
    opacity: screenOpacity.value,
    transform: [{ translateY: screenY.value }],
  }));

  const examQuestionCount = 10;
  const suggestedLessonCount = useMemo(() => {
    if (!data) return 0;
    return Math.min(data.lessons.filter((l) => l.status !== 'locked').length, 4);
  }, [data]);

  if (!safeSlug || !safeUid) {
    return <RuntimeSessionGuard runtimeBase={runtimeBase} slug={safeSlug} />;
  }

  if (loading) {
    return <RuntimeAsyncState state="loading" title="טוען מוכנות למבחן..." description="אנחנו מחשבים מתוך שיעור, תרגול וסקירה איפה את/ה עומד/ת כרגע." />;
  }

  if (error || !data) {
    return (
      <RuntimeAsyncState
        state="error"
        title="לא הצלחנו לטעון את נתוני המוכנות"
        description="ייתכן שהחיבור חלש או ששירות ההערכה עמוס. נסו שוב בעוד רגע או חזרו לתרגול קצר."
        retryLabel="טען שוב מוכנות"
        onRetry={load}
        actionLabel="חזרה לתרגול"
        onAction={() => router.push(buildRuntimeHomePath(runtimeBase, safeSlug, runtimeSession) as any)}
      />
    );
  }

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Animated.View style={[{ gap: 14 }, screenStyle]}>
        <View style={s.topContext}>
          <Pressable style={s.backBtn} onPress={() => router.back()}>
            <Text style={s.backBtnText}>← חזרה</Text>
          </Pressable>
          <Text style={s.eyebrow}>KURSI LEARN · בדיקת מוכנות</Text>
        </View>

        <AnimatedCard delay={40}>
          <BorderGlow color={PRIMARY}>
            <View style={s.hero}>
              <Text style={s.title}>מצב מבחן</Text>
              <Text style={s.subtitle}>בדיקת מוכנות קצרה ומדויקת של {examQuestionCount} שאלות. בסוף מקבלים ציון + צעד המשך ברור.</Text>
              <View style={s.heroInfo}>
                <Text style={s.heroInfoText}>כ-8–12 דקות</Text>
                <Text style={s.heroInfoText}>שאלות מותאמות לרמה שלכם</Text>
              </View>
            </View>
          </BorderGlow>
        </AnimatedCard>

        <AnimatedCard delay={100}>
          <View style={s.card}>
            <Text style={s.cardTitle}>מה תקבלו כאן</Text>
            <Text style={s.cardBody}>• סשן אחד סגור של {examQuestionCount} אינטראקציות</Text>
            <Text style={s.cardBody}>• סיכום מוכנות עם נקודות חזקות וחלשות</Text>
            <Text style={s.cardBody}>• המלצה ברורה לצעד הבא (שיעור / תרגול / סקירה)</Text>
          </View>
        </AnimatedCard>

        <AnimatedCard delay={160}>
          <ElectricBorder color="#22D3EE">
            <View style={s.card}>
              <Text style={s.cardTitle}>כיסוי משוער</Text>
              <Text style={s.cardBody}>נבנה מהנושאים הלא-נעולים שלך, עם עדיפות לנושאים חלשים.</Text>
              <Text style={s.cardBody}>שיעורים זמינים לסשן: {data.lessons.filter((l) => l.status !== 'locked').length}</Text>
              <Text style={s.cardBody}>שיעורים שיופיעו כנראה בסשן: ~{suggestedLessonCount}</Text>
            </View>
          </ElectricBorder>
        </AnimatedCard>

        <AnimatedCard delay={220}>
          <ClickSpark
            sparkColor={PRIMARY}
            onPress={() =>
              router.push(
                buildRuntimeExamSessionPath(runtimeBase, safeSlug, runtimeSession, {
                  target: String(examQuestionCount),
                }) as any,
              )
            }
          >
            <View style={s.primaryBtn}>
              <Text style={s.primaryBtnText}>התחל בדיקת מוכנות</Text>
            </View>
          </ClickSpark>
        </AnimatedCard>

        <AnimatedCard delay={280}>
          <Pressable
            style={s.secondaryBtn}
            onPress={() =>
              router.push(
                buildRuntimeHomePath(runtimeBase, safeSlug, runtimeSession) as any,
              )
            }
          >
            <Text style={s.secondaryBtnText}>חזור לבית הקורס</Text>
          </Pressable>
        </AnimatedCard>
      </Animated.View>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, maxWidth: 700, width: '100%', alignSelf: 'center' as any, paddingTop: 30, paddingBottom: 40 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: BG },
  topContext: { gap: 10, alignItems: 'flex-end' },
  backBtn: { borderWidth: 1, borderColor: BORDER, backgroundColor: '#121126', borderRadius: 999, paddingVertical: 8, paddingHorizontal: 14 },
  backBtnText: { color: TEXT_MED, fontFamily: theme.fonts.medium, fontSize: 12 },
  eyebrow: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 12 },
  hero: { backgroundColor: '#121126', borderColor: BORDER, borderWidth: 1, borderRadius: 16, padding: 16, gap: 10 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 30 },
  subtitle: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 22 },
  heroInfo: { gap: 4 },
  heroInfoText: { color: TEXT, fontFamily: theme.fonts.medium, fontSize: 12, textAlign: 'right' },
  card: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 8 },
  cardTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18, textAlign: 'right' },
  cardBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 12, alignItems: 'center', paddingVertical: 14, marginTop: 4 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 16 },
  secondaryBtn: { borderWidth: 1, borderColor: BORDER, borderRadius: 12, alignItems: 'center', paddingVertical: 11 },
  secondaryBtnText: { color: TEXT, fontFamily: theme.fonts.bold },
  retryBtn: { marginTop: 10, backgroundColor: PRIMARY, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 10 },
  retryText: { color: '#fff', fontFamily: theme.fonts.bold },
  error: { color: '#fff', fontFamily: theme.fonts.bold },
});
